import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savedCount, setSavedCount] = useState(0);

  const refreshSavedCount = useCallback(async () => {
    if (!localStorage.getItem("sms_token")) { setSavedCount(0); return; }
    try {
      const [items, vids] = await Promise.all([
        api.get("/saved-items").then((r) => (r.data.items || []).length).catch(() => 0),
        api.get("/saved-videos/ids").then((r) => (r.data.ids || []).length).catch(() => 0),
      ]);
      setSavedCount(items + vids);
    } catch { /* ignore */ }
  }, []);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("sms_token");
    if (!token) {
      setUser(null);
      setLoading(false);
      return null;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch (e) {
      localStorage.removeItem("sms_token");
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If returning from Google OAuth, let AuthCallback handle it.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const applyAuth = (data) => {
    localStorage.setItem("sms_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const login = async (email, password) => applyAuth((await api.post("/auth/login", { email, password })).data);
  const register = async (email, password, username, name, avatar) => {
    const data = (await api.post("/auth/register", { email, password, username, name })).data;
    const u = applyAuth(data);
    if (avatar && (avatar.emoji || avatar.tag)) {
      try { await api.post("/me/avatar", avatar); return await refresh(); } catch { /* avatar optional */ }
    }
    return u;
  };
  const googleLogin = async (session_id) => applyAuth((await api.post("/auth/google", { session_id })).data);
  const adminLogin = async (passcode) => applyAuth((await api.post("/auth/admin", { passcode })).data);

  const logout = () => {
    localStorage.removeItem("sms_token");
    setUser(null);
    setSavedCount(0);
  };

  useEffect(() => {
    if (user) refreshSavedCount(); else setSavedCount(0);
  }, [user?.user_id, refreshSavedCount]);

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, register, googleLogin, adminLogin, logout, refresh, savedCount, refreshSavedCount }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
